import classNames from 'classnames';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { FC } from 'react';
import Skeleton from 'react-loading-skeleton';
import Avatar from '~/components/Avatar';
import SkeletonByTimeout from '~/components/Skeletons/SkeletonByTimeout';
import Star from '~/components/Star';
import Text from '~/components/Text';
import { PATH } from '~/constants/enum';
import { ReviewItem } from '~/data/product/interfaces';
import { getUrlCustom } from '~/utils/theme';
import styles from './ClientReviewItem.module.scss';
dayjs.extend(relativeTime);

export interface ClientReviewItemProps {
    data?: ReviewItem;
    className?: string;
    onClickImage?: (data?: ReviewItem) => void;
}

const ClientReviewItem: FC<ClientReviewItemProps> = ({ data, className, onClickImage }) => {
    return (
        <div className={classNames(styles.client_review, className, 'mason-grid')}>
            <div className="flex   gap-2">
                {data?.nickname ? (
                    <Avatar size="small" name={data?.nickname || 'Anonymous'} />
                ) : (
                    <SkeletonByTimeout>
                        <Skeleton circle={true} width={32} height={32} />
                    </SkeletonByTimeout>
                )}
                <div className="flex flex-col ">
                    {data?.nickname ? (
                        <Text type="title-4" className="flex-1 line-clamp-1">
                            {data?.nickname ? data?.nickname : 'Anonymous'}
                        </Text>
                    ) : (
                        <SkeletonByTimeout>
                            <Skeleton width={200} />
                        </SkeletonByTimeout>
                    )}
                    {data?.created_at && (
                        <Text type="body-3" className="text-grey-700">
                            {dayjs(data?.created_at).fromNow()}
                        </Text>
                    )}
                </div>
            </div>
            <div className="flex flex-col gap-2 flex-1 ">
                <Star size={16} value={((data?.average_rating || 0) * 5) / 100} />
                {data?.text ? (
                    <Text type="body-2" className="text-grey-900 line-clamp-4">
                        {data?.text}
                    </Text>
                ) : (
                    <SkeletonByTimeout>
                        <Skeleton count={2} />
                    </SkeletonByTimeout>
                )}
                <div className="relative w-full max-h-[300px] flex-1">
                    {data?.images ? (
                        <img
                            onClick={() => onClickImage?.(data)}
                            className="object-cover w-full h-full hover:scale-105 transform transition-transform cursor-pointer"
                            src={data?.images?.[0] ?? data?.product?.small_image?.url}
                            alt={`${data?.nickname}'s feedback`}
                        />
                    ) : (
                        <SkeletonByTimeout>
                            <Skeleton
                                style={{
                                    width: '100%',
                                    height: '100%'
                                }}
                            />
                        </SkeletonByTimeout>
                    )}
                </div>
            </div>
            {!!data?.product && data.product?.sku !== 'REVIEW' && (
                <div className="flex gap-2 border border-t ">
                    <div className="relative flex-shrink-0 basis-[70px] aspect-square">
                        {data?.product?.small_image?.url ? (
                            <img
                                className="object-cover"
                                src={data?.product?.small_image?.url || ''}
                                alt={data?.product?.name || ''}
                            />
                        ) : (
                            <SkeletonByTimeout>
                                <Skeleton
                                    style={{
                                        width: '100%',
                                        height: '100%'
                                    }}
                                />
                            </SkeletonByTimeout>
                        )}
                    </div>
                    <div className="md:line-clamp-3 mb-1 ">
                        <a
                            className="cursor-pointer 
              transform duration-300 hover:underline text-xs text-text-light"
                            href={getUrlCustom({
                                pathname: PATH.ProductDetail,
                                query: {
                                    slug: data.product?.url_key,
                                    variant: data.product?.variants?.[0]?.product?.id
                                }
                            })}
                        >
                            <span className="ms-1 inline">{data.product?.name}</span>
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientReviewItem;
